import React, {
  memo,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  alpha,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Divider,
  Popover,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import {
  aiGradientButtonSx,
  getAiOverlayHeaderSx,
  getAiOverlayPaperSx,
  getAiOverlayShellSx,
  getAiOverlayIconBoxSx,
  getAiOverlayBodyTextSx,
  getAiOverlayTitleSx,
  getAiOverlayUploadButtonSx,
  AI_OVERLAY_ACCENT,
  AI_OVERLAY_ACCENT_2,
} from '../../../shared';
import { commitPosMenuAiData, extractPosMenuAiData } from '../api';

const SUPPORTED_EXTENSIONS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.xlsx',
  '.xls',
  '.csv',
  '.docx',
  '.txt',
];
const ACCEPT_ATTR = SUPPORTED_EXTENSIONS.join(',');

const ACTION_OPTIONS = [
  { value: 'create', label: 'Create new' },
  { value: 'skip', label: 'Skip' },
  { value: 'merge', label: 'Merge existing' },
  { value: 'update', label: 'Update existing' },
];

const DIETARY_OPTIONS = [
  { value: 'veg', label: 'Veg' },
  { value: 'non_veg', label: 'Non-Veg' },
  { value: 'egg', label: 'Egg' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'jain', label: 'Jain' },
];

const emptyMenu = () => ({ categories: [], variations: [], items: [] });
const PREVIEW_ROWS_PER_PAGE = 20;
const normName = (value) => String(value || '').trim().toLowerCase();

/** Rebuild Parent → Children tree from item parent_category / child_category fields. */
const rebuildCategoriesFromItems = (items, existingCategories = []) => {
  const prevByParent = new Map();
  (existingCategories || []).forEach((cat) => {
    if (cat?.name) prevByParent.set(normName(cat.name), cat);
  });
  const tree = new Map(); // parentName -> Map(childNorm -> childName)
  (items || []).forEach((item) => {
    const parent = String(item?.parent_category || '').trim() || 'Main Menu';
    const child = String(item?.child_category || '').trim();
    if (!tree.has(parent)) tree.set(parent, new Map());
    if (child) tree.get(parent).set(normName(child), child);
  });
  let idx = 0;
  return Array.from(tree.entries()).map(([parent, childrenMap]) => {
    const prev = prevByParent.get(normName(parent));
    const tempId = prev?.temp_id || `cat-sync-${Date.now()}-${idx++}`;
    return {
      temp_id: tempId,
      name: parent,
      action: prev?.action || 'create',
      match_id: prev?.match_id ?? null,
      match_name: prev?.match_name ?? null,
      children: Array.from(childrenMap.values()).map((name, cIdx) => ({
        name,
        temp_id: `${tempId}-child-${cIdx}`,
        action: 'create',
        match_id: null,
        match_name: null,
      })),
    };
  });
};

const isRequestCanceled = (err) =>
  err?.code === 'ERR_CANCELED' ||
  err?.name === 'CanceledError' ||
  err?.name === 'AbortError' ||
  Boolean(err?.__CANCEL__);

/** Live URL validation — empty is OK; partial/invalid shows an error while typing. */
const getMenuUrlError = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/\s/.test(raw)) return 'URL cannot contain spaces.';
  let candidate = raw;
  if (!/^https?:\/\//i.test(candidate)) {
    candidate = `https://${candidate}`;
  }
  try {
    const parsed = new URL(candidate);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return 'Only http(s) website links are supported.';
    }
    const host = (parsed.hostname || '').toLowerCase();
    if (
      !host ||
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host.endsWith('.local') ||
      host.endsWith('.internal')
    ) {
      return 'Enter a public menu website URL.';
    }
    // Require a real domain with TLD (e.g. restaurant.com)
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(host)) {
      return 'Enter a valid website URL (e.g. https://restaurant.com/menu).';
    }
    const tld = host.split('.').pop() || '';
    if (tld.length < 2) {
      return 'Enter a valid website URL (e.g. https://restaurant.com/menu).';
    }
    return '';
  } catch {
    return 'Enter a valid website URL (e.g. https://restaurant.com/menu).';
  }
};

const looksLikeUrl = (value) => Boolean(String(value || '').trim()) && !getMenuUrlError(value);

const getCompactFieldSx = (isDark) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.25,
    fontSize: '0.8rem',
    bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#0f172a', 0.02),
  },
  '& .MuiOutlinedInput-input': { py: 0.6, px: 1 },
  '& .MuiSelect-select': { py: '5.5px !important' },
});

const getSectionCardSx = (isDark, accent) => ({
  p: 1.25,
  borderRadius: 2,
  borderColor: alpha(accent, isDark ? 0.22 : 0.18),
  bgcolor: 'transparent',
  backgroundImage: 'none',
  overflow: 'visible',
  minWidth: 0,
});

const sectionHeadSx = {
  fontWeight: 750,
  fontSize: '0.78rem',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const getDenseTableSx = (isDark, accent) => ({
  '& .MuiTableCell-root': { py: 0.5, px: 0.75, borderColor: alpha(accent, 0.1) },
  '& .MuiTableCell-head': {
    py: 0.75,
    fontSize: '0.68rem',
    fontWeight: 750,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: isDark ? 'rgba(148,163,184,0.9)' : '#64748b',
    bgcolor: isDark ? '#111c33' : '#f8fafc',
  },
});

/**
 * Own expanded state so Edit/Hide does not re-render the heavy items table.
 */
const ReviewCategoriesPanel = memo(function ReviewCategoriesPanel({
  categories,
  isDark,
  accent,
  bodyTextSx,
  onUpdate,
  onRemove,
}) {
  const [expanded, setExpanded] = useState(false);
  const compactFieldSx = useMemo(() => getCompactFieldSx(isDark), [isDark]);
  const denseTableSx = useMemo(() => getDenseTableSx(isDark, accent), [isDark, accent]);
  const count = categories.length;

  return (
    <Paper variant="outlined" sx={getSectionCardSx(isDark, accent)}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
        <Typography sx={sectionHeadSx}>Categories ({count})</Typography>
        <Button
          type="button"
          size="small"
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ textTransform: 'none', py: 0, minWidth: 0, fontSize: '0.74rem' }}
        >
          {expanded ? 'Hide' : 'Edit'}
        </Button>
      </Stack>
      {!expanded ? (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          {categories.slice(0, 16).map((row) => {
            const children = (row.children || [])
              .map((c) => (typeof c === 'string' ? c : c?.name))
              .filter(Boolean);
            if (children.length === 0) {
              return (
                <Chip
                  key={row.temp_id}
                  size="small"
                  label={row.name || 'Category'}
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              );
            }
            return children.slice(0, 6).map((childName, idx) => (
              <Chip
                key={`${row.temp_id}-c-${idx}`}
                size="small"
                label={`${row.name || 'Parent'} › ${childName}`}
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            ));
          })}
          {count > 16 ? (
            <Chip size="small" label={`+${count - 16} parents`} sx={{ height: 22, fontSize: '0.7rem' }} />
          ) : null}
        </Stack>
      ) : (
        <TableContainer sx={{ maxHeight: 260, width: '100%', overflow: 'auto' }}>
          <Table size="small" stickyHeader sx={denseTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Parent Category</TableCell>
                <TableCell>Child Categories</TableCell>
                <TableCell width={118}>Action</TableCell>
                <TableCell width={36} />
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((row) => (
                <TableRow key={row.temp_id}>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      sx={compactFieldSx}
                      value={row.name || ''}
                      onChange={(e) => onUpdate(row.temp_id, { name: e.target.value })}
                      placeholder="Parent category"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      sx={compactFieldSx}
                      value={(row.children || []).map((c) => (typeof c === 'string' ? c : c.name)).join(', ')}
                      onChange={(e) =>
                        onUpdate(row.temp_id, {
                          children: e.target.value
                            .split(',')
                            .map((name) => name.trim())
                            .filter(Boolean)
                            .map((name, idx) => ({
                              name,
                              temp_id: `${row.temp_id}-child-${idx}`,
                              action: 'create',
                            })),
                        })
                      }
                      placeholder="Child categories, comma separated"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      sx={compactFieldSx}
                      value={row.action || 'create'}
                      onChange={(e) => onUpdate(row.temp_id, { action: e.target.value })}
                    >
                      {ACTION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => onRemove(row.temp_id)}>
                      <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {count === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="caption" sx={bodyTextSx}>
                      No categories extracted.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
});

const ReviewVariationsPanel = memo(function ReviewVariationsPanel({
  variations,
  isDark,
  accent,
  bodyTextSx,
  onUpdate,
  onRemove,
  onRenamePropagate,
}) {
  const [expanded, setExpanded] = useState(false);
  const compactFieldSx = useMemo(() => getCompactFieldSx(isDark), [isDark]);
  const denseTableSx = useMemo(() => getDenseTableSx(isDark, accent), [isDark, accent]);
  const count = variations.length;

  return (
    <Paper variant="outlined" sx={getSectionCardSx(isDark, accent)}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
        <Typography sx={sectionHeadSx}>Variations ({count})</Typography>
        <Button
          type="button"
          size="small"
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ textTransform: 'none', py: 0, minWidth: 0, fontSize: '0.74rem' }}
        >
          {expanded ? 'Hide' : 'Edit'}
        </Button>
      </Stack>
      {!expanded ? (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          {variations.slice(0, 24).map((row) => (
            <Chip
              key={row.temp_id}
              size="small"
              label={row.name || 'Variation'}
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          ))}
          {count === 0 ? (
            <Typography variant="caption" sx={bodyTextSx}>
              No shared variations yet — add them on item rows.
            </Typography>
          ) : null}
          {count > 24 ? (
            <Chip size="small" label={`+${count - 24} more`} sx={{ height: 22, fontSize: '0.7rem' }} />
          ) : null}
        </Stack>
      ) : (
        <TableContainer sx={{ maxHeight: 220, width: '100%', overflow: 'auto' }}>
          <Table size="small" stickyHeader sx={denseTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell width={130}>Action</TableCell>
                <TableCell width={36} />
              </TableRow>
            </TableHead>
            <TableBody>
              {variations.map((row) => (
                <TableRow key={row.temp_id}>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      sx={compactFieldSx}
                      value={row.name || ''}
                      onFocus={(e) => {
                        e.target.dataset.prevName = row.name || '';
                      }}
                      onChange={(e) => onUpdate(row.temp_id, { name: e.target.value })}
                      onBlur={(e) => {
                        const prevName = e.target.dataset.prevName || '';
                        onRenamePropagate(prevName, e.target.value);
                      }}
                      placeholder="Variation name"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      sx={compactFieldSx}
                      value={row.action || 'create'}
                      onChange={(e) => onUpdate(row.temp_id, { action: e.target.value })}
                    >
                      {ACTION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => onRemove(row.temp_id)}>
                      <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {count === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="caption" sx={bodyTextSx}>
                      Empty catalog. Add variations on menu item rows and they will appear here.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
});

/**
 * AI Menu overlay — same card layout as Stock AI Invoice.
 * Menu file or website URL → extract → preview/edit → commit.
 */
function PosAiMenuUploaderOverlay({ open, onClose, onSuccess, isDark }) {
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | preview
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [menuUrl, setMenuUrl] = useState('');
  const [menu, setMenu] = useState(emptyMenu());
  const [itemsPage, setItemsPage] = useState(0);
  const [itemsRowsPerPage, setItemsRowsPerPage] = useState(PREVIEW_ROWS_PER_PAGE);
  const [variationEditor, setVariationEditor] = useState(null); // { anchorEl, itemTempId, index }
  const [loadingHint, setLoadingHint] = useState('Reading menu…');

  const menuUrlError = useMemo(
    () => (selectedFile ? '' : getMenuUrlError(menuUrl)),
    [menuUrl, selectedFile]
  );

  const abortInFlight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    abortInFlight();
    setLoading(false);
    onClose?.();
  }, [abortInFlight, onClose]);

  useEffect(() => {
    if (!open) {
      abortInFlight();
      setError('');
      setLoading(false);
      setCommitting(false);
      setSelectedFile(null);
      setMenuUrl('');
      setStep('upload');
      setMenu(emptyMenu());
      setItemsPage(0);
      setVariationEditor(null);
      setLoadingHint('Reading menu…');
    }
  }, [open, abortInFlight]);

  const counts = useMemo(
    () => ({
      categories: (menu.categories || []).length,
      items: (menu.items || []).length,
      variations: (menu.variations || []).length,
      duplicates: (menu.items || []).filter((row) => row.match_id).length,
    }),
    [menu]
  );

  const pagedItems = useMemo(() => {
    const items = menu.items || [];
    const start = itemsPage * itemsRowsPerPage;
    return items.slice(start, start + itemsRowsPerPage);
  }, [menu.items, itemsPage, itemsRowsPerPage]);

  const canExtract =
    Boolean(selectedFile) || (looksLikeUrl(menuUrl) && !menuUrlError);

  const handlePickFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const lower = file.name.toLowerCase();
    const ok = SUPPORTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
    if (!ok) {
      setError('Unsupported file type. Use PDF, image, Excel, CSV, DOCX, or text.');
      setSelectedFile(null);
      return;
    }
    setError('');
    setMenuUrl('');
    setSelectedFile(file);
  };

  const runExtraction = useCallback(async () => {
    const trimmedUrl = menuUrl.trim();
    if (!selectedFile) {
      const urlErr = getMenuUrlError(trimmedUrl);
      if (!trimmedUrl || urlErr) {
        setError(urlErr || 'Choose a menu file, or paste a menu website URL.');
        return;
      }
    }
    setError('');
    setLoading(true);
    setLoadingHint(
      selectedFile
        ? 'Reading menu image…'
        : 'Opening menu website and extracting items…'
    );
    abortInFlight();
    const controller = new AbortController();
    abortRef.current = controller;
    let previewScheduled = false;
    try {
      const response = await extractPosMenuAiData({
        file: selectedFile || undefined,
        url: !selectedFile && trimmedUrl ? trimmedUrl : undefined,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const extracted = response?.extracted_data || emptyMenu();
      const nextMenu = {
        categories: Array.isArray(extracted.categories) ? extracted.categories : [],
        variations: Array.isArray(extracted.variations) ? extracted.variations : [],
        items: Array.isArray(extracted.items) ? extracted.items : [],
      };
      // Yield to the browser before mounting the large preview table.
      await new Promise((resolve) => window.setTimeout(resolve, 30));
      if (controller.signal.aborted) return;
      setLoadingHint('Preparing menu review…');
      previewScheduled = true;
      startTransition(() => {
        setMenu(nextMenu);
        setItemsPage(0);
        setStep('preview');
        // Keep the loading screen visible until the review UI is ready to commit.
        setLoading(false);
      });
    } catch (err) {
      if (isRequestCanceled(err) || controller.signal.aborted) {
        return;
      }
      const msg =
        err?.userMessage ||
        err?.response?.data?.error ||
        err?.message ||
        'Could not read this menu';
      setError(typeof msg === 'string' ? msg : 'Could not read this menu');
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      if (!controller.signal.aborted && !previewScheduled) {
        setLoading(false);
      }
    }
  }, [selectedFile, menuUrl, abortInFlight]);

  const updateItem = (tempId, patch) => {
    setMenu((prev) => {
      const items = prev.items.map((row) => (row.temp_id === tempId ? { ...row, ...patch } : row));
      const touchesCategory =
        Object.prototype.hasOwnProperty.call(patch, 'parent_category') ||
        Object.prototype.hasOwnProperty.call(patch, 'child_category');
      return {
        ...prev,
        items,
        categories: touchesCategory
          ? rebuildCategoriesFromItems(items, prev.categories)
          : prev.categories,
      };
    });
  };

  const removeItem = (tempId) => {
    setMenu((prev) => {
      const items = prev.items.filter((row) => row.temp_id !== tempId);
      return {
        ...prev,
        items,
        categories: rebuildCategoriesFromItems(items, prev.categories),
      };
    });
  };

  const updateCategory = useCallback((tempId, patch) => {
    setMenu((prev) => {
      const old = prev.categories.find((row) => row.temp_id === tempId);
      const categories = prev.categories.map((row) =>
        row.temp_id === tempId ? { ...row, ...patch } : row
      );
      let items = prev.items;
      if (old && patch.name != null && String(patch.name) !== String(old.name || '')) {
        const oldName = old.name || '';
        const newName = patch.name;
        items = items.map((item) =>
          item.parent_category === oldName ? { ...item, parent_category: newName } : item
        );
      }
      if (old && Array.isArray(patch.children)) {
        const oldChildren = (old.children || [])
          .map((c) => (typeof c === 'string' ? c : c?.name))
          .filter(Boolean);
        const newChildren = patch.children
          .map((c) => (typeof c === 'string' ? c : c?.name))
          .filter(Boolean);
        // Single-child rename: propagate to items under this parent.
        if (oldChildren.length === 1 && newChildren.length === 1 && oldChildren[0] !== newChildren[0]) {
          const parentName = patch.name != null ? patch.name : old.name;
          items = items.map((item) =>
            item.parent_category === parentName && item.child_category === oldChildren[0]
              ? { ...item, child_category: newChildren[0] }
              : item
          );
        }
      }
      return { ...prev, categories, items };
    });
  }, []);

  const removeCategory = useCallback((tempId) => {
    setMenu((prev) => ({
      ...prev,
      categories: prev.categories.filter((row) => row.temp_id !== tempId),
    }));
  }, []);

  const ensureCatalogHasVariation = (prev, name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return prev.variations || [];
    const key = normName(trimmed);
    const existing = prev.variations || [];
    if (existing.some((row) => normName(row.name) === key)) return existing;
    return [
      ...existing,
      {
        temp_id: `var-local-${Date.now()}-${existing.length}`,
        name: trimmed,
        department_name: 'portion',
        additional_price: 0,
        action: 'create',
        match_id: null,
        match_name: null,
      },
    ];
  };

  const propagateVariationRename = useCallback((oldName, newName) => {
    const oldKey = normName(oldName);
    const trimmedNew = String(newName || '').trim();
    if (!oldKey || !trimmedNew || oldKey === normName(trimmedNew)) return;
    setMenu((prev) => ({
      ...prev,
      variations: (prev.variations || []).map((row) =>
        normName(row.name) === oldKey ? { ...row, name: trimmedNew } : row
      ),
      items: (prev.items || []).map((item) => ({
        ...item,
        variations: (item.variations || []).map((vrow) =>
          normName(vrow.name) === oldKey ? { ...vrow, name: trimmedNew } : vrow
        ),
      })),
    }));
  }, []);

  const updateVariation = useCallback((tempId, patch) => {
    setMenu((prev) => ({
      ...prev,
      variations: (prev.variations || []).map((row) =>
        row.temp_id === tempId ? { ...row, ...patch } : row
      ),
    }));
  }, []);

  const removeCatalogVariation = useCallback((tempId) => {
    setMenu((prev) => {
      const current = (prev.variations || []).find((row) => row.temp_id === tempId);
      if (!current) return prev;
      const key = normName(current.name);
      return {
        ...prev,
        variations: (prev.variations || []).filter((row) => row.temp_id !== tempId),
        items: (prev.items || []).map((item) => ({
          ...item,
          variations: (item.variations || []).filter((vrow) => normName(vrow.name) !== key),
        })),
      };
    });
  }, []);

  const updateItemVariation = (itemTempId, index, patch) => {
    setMenu((prev) => {
      let nextVariationsCatalog = prev.variations || [];
      const items = (prev.items || []).map((row) => {
        if (row.temp_id !== itemTempId) return row;
        const list = [...(row.variations || [])];
        const current = list[index] || { name: '', price: 0 };
        const next = { ...current, ...patch };
        if (patch.name !== undefined) {
          next.name = String(patch.name || '');
          nextVariationsCatalog = ensureCatalogHasVariation(
            { ...prev, variations: nextVariationsCatalog },
            next.name
          );
        }
        list[index] = next;
        return { ...row, variations: list };
      });
      return { ...prev, items, variations: nextVariationsCatalog };
    });
  };

  const addItemVariation = (itemTempId) => {
    setMenu((prev) => ({
      ...prev,
      items: (prev.items || []).map((row) =>
        row.temp_id === itemTempId
          ? { ...row, variations: [...(row.variations || []), { name: '', price: 0 }] }
          : row
      ),
    }));
  };

  const removeItemVariation = (itemTempId, index) => {
    setMenu((prev) => ({
      ...prev,
      items: (prev.items || []).map((row) => {
        if (row.temp_id !== itemTempId) return row;
        return {
          ...row,
          variations: (row.variations || []).filter((_, idx) => idx !== index),
        };
      }),
    }));
    setVariationEditor((prev) => {
      if (!prev || prev.itemTempId !== itemTempId) return prev;
      if (prev.index === index) return null;
      if (prev.index > index) return { ...prev, index: prev.index - 1 };
      return prev;
    });
  };

  const handleCommit = async () => {
    setError('');
    setCommitting(true);
    try {
      const response = await commitPosMenuAiData(menu);
      onSuccess?.(response?.summary || {});
      onClose?.();
    } catch (err) {
      const msg =
        err?.userMessage ||
        err?.response?.data?.error ||
        err?.message ||
        'Could not save menu';
      setError(typeof msg === 'string' ? msg : 'Could not save menu');
    } finally {
      setCommitting(false);
    }
  };

  if (!open) return null;

  const accent = AI_OVERLAY_ACCENT;
  const accent2 = AI_OVERLAY_ACCENT_2;
  const bodyTextSx = getAiOverlayBodyTextSx(isDark);
  const titleSx = getAiOverlayTitleSx(isDark);

  if (step === 'preview') {
    const compactFieldSx = getCompactFieldSx(isDark);
    const sectionCardSx = getSectionCardSx(isDark, accent);
    const denseTableSx = getDenseTableSx(isDark, accent);
    return (
      <Dialog
        open
        fullWidth
        maxWidth="lg"
        onClose={committing ? undefined : onClose}
        PaperProps={{
          sx: {
            bgcolor: isDark ? '#0f172a' : '#fff',
            backgroundImage: 'none',
            borderRadius: 2.5,
            border: `1px solid ${alpha(accent, 0.28)}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 1.25,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            <AutoAwesomeIcon sx={{ color: accent, fontSize: 20 }} />
            <Typography sx={{ ...titleSx, fontSize: '0.98rem' }}>Review AI Menu</Typography>
          </Stack>
          <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
            <Chip size="small" label={`${counts.items} items`} sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: alpha(accent, isDark ? 0.16 : 0.1), color: accent }} />
            <Chip size="small" label={`${counts.categories} categories`} sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: alpha(accent, isDark ? 0.16 : 0.1), color: accent }} />
            <Chip size="small" label={`${counts.variations} variations`} sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: alpha(accent, isDark ? 0.16 : 0.1), color: accent }} />
            {counts.duplicates ? (
              <Chip size="small" label={`${counts.duplicates} duplicates`} color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700 }} />
            ) : null}
            <IconButton onClick={onClose} disabled={committing} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            maxHeight: '72vh',
            p: { xs: 1.25, sm: 1.75 },
            bgcolor: isDark ? alpha('#0b1120', 0.4) : alpha('#f8fafc', 0.65),
          }}
        >
          {error ? (
            <Typography color="error" variant="body2" sx={{ mb: 1.5 }}>
              {error}
            </Typography>
          ) : null}
          {committing ? (
            <Box sx={{ mb: 1.5 }}>
              <LinearProgress
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: alpha(accent, 0.12),
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, ${accent2} 0%, ${accent} 100%)`,
                  },
                }}
              />
              <Typography variant="caption" sx={{ ...bodyTextSx, mt: 0.75, display: 'block' }}>
                Saving categories, variations, and menu items…
              </Typography>
            </Box>
          ) : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 1.25,
              mb: 1.25,
              alignItems: 'start',
            }}
          >
            <ReviewCategoriesPanel
              categories={menu.categories || []}
              isDark={isDark}
              accent={accent}
              bodyTextSx={bodyTextSx}
              onUpdate={updateCategory}
              onRemove={removeCategory}
            />
            <ReviewVariationsPanel
              variations={menu.variations || []}
              isDark={isDark}
              accent={accent}
              bodyTextSx={bodyTextSx}
              onUpdate={updateVariation}
              onRemove={removeCatalogVariation}
              onRenamePropagate={propagateVariationRename}
            />
          </Box>

          <Paper variant="outlined" sx={{ ...sectionCardSx, p: 0, overflow: 'hidden' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.25, py: 0.9, borderBottom: `1px solid ${alpha(accent, 0.12)}` }}
            >
              <Typography sx={sectionHeadSx}>Menu items ({counts.items})</Typography>
            </Stack>
            <TableContainer sx={{ maxHeight: '46vh' }}>
              <Table size="small" stickyHeader sx={denseTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell width={86} sx={{ minWidth: 86 }}>
                      Price
                    </TableCell>
                    <TableCell width={170} sx={{ minWidth: 150 }}>
                      Variations
                    </TableCell>
                    <TableCell width={96}>Dietary</TableCell>
                    <TableCell width={120}>Parent</TableCell>
                    <TableCell width={120}>Child</TableCell>
                    <TableCell width={112}>Action</TableCell>
                    <TableCell width={36} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedItems.map((row) => {
                    const itemVariations = row.variations || [];
                    return (
                      <TableRow key={row.temp_id} hover>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                            value={row.name || ''}
                            onChange={(e) => updateItem(row.temp_id, { name: e.target.value })}
                          />
                          {row.description ? (
                            <Typography
                              variant="caption"
                              sx={{
                                ...bodyTextSx,
                                display: 'block',
                                mt: 0.25,
                                fontSize: '0.68rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 260,
                              }}
                              title={row.description}
                            >
                              {row.description}
                            </Typography>
                          ) : null}
                          {row.match_name ? (
                            <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.68rem' }}>
                              Duplicate of: {row.match_name}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ minWidth: 86, width: 86 }}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={row.price ?? ''}
                            onChange={(e) => updateItem(row.temp_id, { price: e.target.value })}
                            inputProps={{
                              step: 'any',
                              min: 0,
                              style: { textAlign: 'right', fontWeight: 600 },
                            }}
                            sx={{
                              ...compactFieldSx,
                              '& input[type=number]': {
                                MozAppearance: 'textfield',
                              },
                              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                                {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.4} useFlexGap flexWrap="wrap" alignItems="center">
                            {itemVariations.length === 0 ? (
                              <Typography variant="caption" sx={{ ...bodyTextSx, mr: 0.25, fontSize: '0.7rem' }}>
                                None
                              </Typography>
                            ) : (
                              itemVariations.map((vrow, vIdx) => (
                                <Chip
                                  key={`${row.temp_id}-var-${vIdx}`}
                                  size="small"
                                  label={`${vrow.name || 'Untitled'}${
                                    vrow.price !== undefined && vrow.price !== '' ? ` · ${vrow.price}` : ''
                                  }`}
                                  onClick={(e) =>
                                    setVariationEditor({
                                      anchorEl: e.currentTarget,
                                      itemTempId: row.temp_id,
                                      index: vIdx,
                                    })
                                  }
                                  onDelete={() => removeItemVariation(row.temp_id, vIdx)}
                                  sx={{ fontWeight: 600, maxWidth: 140, height: 22, fontSize: '0.7rem' }}
                                />
                              ))
                            )}
                            <IconButton
                              size="small"
                              aria-label="Add variation"
                              onClick={(e) => {
                                const nextIndex = itemVariations.length;
                                addItemVariation(row.temp_id);
                                setVariationEditor({
                                  anchorEl: e.currentTarget,
                                  itemTempId: row.temp_id,
                                  index: nextIndex,
                                });
                              }}
                              sx={{ color: accent, p: 0.25 }}
                            >
                              <AddIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                            value={row.dietary || 'veg'}
                            onChange={(e) => updateItem(row.temp_id, { dietary: e.target.value })}
                          >
                            {DIETARY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                            value={row.parent_category || ''}
                            onChange={(e) => updateItem(row.temp_id, { parent_category: e.target.value })}
                            placeholder="Parent"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                            value={row.child_category || ''}
                            onChange={(e) => updateItem(row.temp_id, { child_category: e.target.value })}
                            placeholder="Child"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                            value={row.action || 'create'}
                            onChange={(e) => updateItem(row.temp_id, { action: e.target.value })}
                          >
                            {ACTION_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeItem(row.temp_id)}>
                            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={(menu.items || []).length}
              page={itemsPage}
              onPageChange={(_, nextPage) => setItemsPage(nextPage)}
              rowsPerPage={itemsRowsPerPage}
              onRowsPerPageChange={(event) => {
                setItemsRowsPerPage(Number(event.target.value) || PREVIEW_ROWS_PER_PAGE);
                setItemsPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{
                borderTop: `1px solid ${alpha(accent, 0.12)}`,
                '& .MuiTablePagination-toolbar': { minHeight: 40 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.72rem',
                },
              }}
            />
          </Paper>

          <Popover
            open={Boolean(variationEditor?.anchorEl)}
            anchorEl={variationEditor?.anchorEl || null}
            onClose={() => setVariationEditor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  p: 1.5,
                  width: 260,
                  bgcolor: isDark ? '#0f172a' : '#fff',
                  border: `1px solid ${alpha(accent, 0.28)}`,
                },
              },
            }}
          >
            {variationEditor ? (
              <Stack spacing={1.25}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: accent }}>
                  Edit variation
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  label="Name"
                  autoFocus
                  value={
                    (menu.items || []).find((item) => item.temp_id === variationEditor.itemTempId)
                      ?.variations?.[variationEditor.index]?.name || ''
                  }
                  onChange={(e) =>
                    updateItemVariation(variationEditor.itemTempId, variationEditor.index, {
                      name: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="Price"
                  value={
                    (menu.items || []).find((item) => item.temp_id === variationEditor.itemTempId)
                      ?.variations?.[variationEditor.index]?.price ?? ''
                  }
                  onChange={(e) =>
                    updateItemVariation(variationEditor.itemTempId, variationEditor.index, {
                      price: e.target.value,
                    })
                  }
                  inputProps={{ step: 'any', min: 0 }}
                />
                <Stack direction="row" justifyContent="space-between">
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      removeItemVariation(variationEditor.itemTempId, variationEditor.index);
                      setVariationEditor(null);
                    }}
                  >
                    Remove
                  </Button>
                  <Button size="small" variant="contained" onClick={() => setVariationEditor(null)} sx={aiGradientButtonSx()}>
                    Done
                  </Button>
                </Stack>
              </Stack>
            ) : null}
          </Popover>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button size="small" onClick={() => setStep('upload')} disabled={committing}>
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={committing || !(menu.items || []).length}
            onClick={handleCommit}
            sx={aiGradientButtonSx({ px: 2 })}
          >
            {committing ? 'Saving…' : 'Confirm & Import'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const dashedBoxSx = {
    mb: 2,
    p: 2,
    borderRadius: 2,
    border: `1px dashed ${alpha(accent, isDark ? 0.35 : 0.28)}`,
    bgcolor: isDark ? alpha(accent, 0.04) : alpha(accent, 0.03),
  };

  return (
    <Box
      sx={getAiOverlayShellSx(isDark, {
        position: 'fixed',
        zIndex: (theme) => theme.zIndex.modal + 2,
        centered: true,
      })}
    >
      <Paper elevation={0} sx={getAiOverlayPaperSx(isDark, accent, accent2)}>
        <Box sx={getAiOverlayHeaderSx(accent, accent2, isDark)}>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" minWidth={0}>
            <Box sx={getAiOverlayIconBoxSx(accent)}>
              <AutoAwesomeIcon sx={{ color: accent, fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                <Typography variant="subtitle1" sx={titleSx}>
                  AI Menu
                </Typography>
                <Chip
                  size="small"
                  label="Gemini"
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    bgcolor: alpha(accent, isDark ? 0.14 : 0.1),
                    color: accent,
                    border: `1px solid ${alpha(accent, 0.28)}`,
                  }}
                />
              </Stack>
              <Typography variant="caption" sx={{ ...bodyTextSx, display: 'block', mt: 0.25 }}>
                Extract categories, prices, and menu items
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close AI menu"
            sx={{ color: isDark ? 'rgba(148, 163, 184, 0.92)' : '#64748b' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            hidden
            onChange={handlePickFile}
          />

          {loading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: alpha(accent, 0.12),
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, ${accent2} 0%, ${accent} 100%)`,
                  },
                }}
              />
              <Typography variant="body2" sx={{ ...bodyTextSx, mt: 1.5, textAlign: 'center' }}>
                {loadingHint}
              </Typography>
            </Box>
          )}

          {!loading && (
            <>
              <Typography variant="body2" sx={{ ...bodyTextSx, mb: 2, lineHeight: 1.55 }}>
                Upload one menu (PDF, image, Excel, or text), or paste a public menu website URL. We&apos;ll
                extract categories, prices, and items for review.
              </Typography>

              <Box sx={dashedBoxSx}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ ...titleSx, fontSize: '0.9rem' }}>
                    Menu file
                  </Typography>
                  {selectedFile && (
                    <Chip
                      size="small"
                      icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                      label="Added"
                      color="success"
                      variant="outlined"
                      onDelete={() => setSelectedFile(null)}
                    />
                  )}
                </Stack>
                <Button
                  fullWidth
                  variant="outlined"
                  size="medium"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={Boolean(menuUrl.trim())}
                  sx={getAiOverlayUploadButtonSx(isDark, accent)}
                >
                  Upload
                </Button>
                <Typography variant="body2" sx={{ ...bodyTextSx, mt: 1, wordBreak: 'break-word' }}>
                  {selectedFile
                    ? selectedFile.name
                    : menuUrl.trim()
                      ? 'Clear the website URL to upload a file instead.'
                      : 'No file selected'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ ...bodyTextSx, display: 'block', mt: 0.75, fontSize: '0.72rem' }}
                >
                  Supported: {SUPPORTED_EXTENSIONS.join(', ')}
                </Typography>
              </Box>

              <Divider sx={{ my: 0.5, mb: 2, borderColor: alpha(accent, 0.18) }}>
                <Typography variant="caption" sx={bodyTextSx}>
                  or website link
                </Typography>
              </Divider>

              <Box sx={{ ...dashedBoxSx, mb: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
                  <LinkIcon sx={{ fontSize: 18, color: accent }} />
                  <Typography variant="subtitle2" sx={{ ...titleSx, fontSize: '0.9rem' }}>
                    Menu website URL
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="https://restaurant.com/menu"
                  value={menuUrl}
                  disabled={Boolean(selectedFile)}
                  error={Boolean(menuUrlError)}
                  onChange={(e) => {
                    setMenuUrl(e.target.value);
                    setError('');
                  }}
                  InputProps={{
                    sx: {
                      bgcolor: isDark ? alpha('#0f172a', 0.45) : alpha('#fff', 0.85),
                    },
                  }}
                  helperText={
                    selectedFile
                      ? 'Clear the file to use a website URL instead.'
                      : menuUrlError || 'Paste a menu page URL for best results.'
                  }
                  FormHelperTextProps={{
                    sx: {
                      ...(menuUrlError
                        ? { color: 'error.main', mx: 0, mt: 1, fontSize: '0.72rem' }
                        : { ...bodyTextSx, mx: 0, mt: 1, fontSize: '0.72rem' }),
                    },
                  }}
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={!canExtract}
                startIcon={<AutoAwesomeIcon />}
                onClick={runExtraction}
                sx={aiGradientButtonSx({
                  mt: 0.5,
                  py: 1.35,
                })}
              >
                Read with AI & extract menu
              </Button>
            </>
          )}

          {error && !loading && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default PosAiMenuUploaderOverlay;
